import React, { useMemo, useState } from 'lib/react';
import {
  EVButton,
  EVAlert, EVChip, EVGrid, EVTextField, EVTypography, makeStyles,
  EVCheckCircleOutlineIcon, EVFormHelperText, EVClear,
} from 'lib/components';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import { useSelector } from 'lib/redux';
import clsx from 'clsx';
import ChipInput from 'commonComponents/chip-input';
import { FILE_FORMAT_NAMES } from 'features/orderHistory/OrderHistory.constants';
import { EMAIL_REGEX, SUCCESS, ERROR } from 'constants/AppConstants';
import emailReportFilesService from 'services/emailReportFiles.service';

const useStyles = makeStyles((theme) => ({
  selectedFilesList: {
    gap: '8px',
    width: 'min-content',
    minWidth: '100%',
  },
  selectedFileChip: {
    backgroundColor: theme.evPalette.charcoal[200],
    padding: '8px',
    color: '#1E1F24',
    '&>span': {
      fontWeight: 400,
      lineHeight: '14px !important',
      overflow: 'visible',
    },
  },
  enterEmailAddressText: {
    fontSize: '16px !important',
    lineHeight: '24px !important',
    width: 'min-content',
    minWidth: '100%',
  },
  emailInput: {
    height: 'unset',
  },
  sendEmailBtn: {
    '&.Mui-disabled': {
      backgroundColor: theme.evPalette.charcoal[600],
      color: theme.evPalette.salt[100],
    },
  },
  alertMsg: {
    color: theme.evPalette.charcoal[900],
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '19px',
  },
  successAlert: { backgroundColor: theme.evPalette.lime[100] },
  successAlertIcon: { '&>svg': { color: theme.evPalette.lime[700] } },
  successEmailList: {
    gap: '6px',
    '&>p': {
      color: theme.evPalette.charcoal[900],
      fontWeight: 400,
      lineHeight: '18px !important',
      width: 'min-content',
      minWidth: '100%',
      '&.listItem': {
        backgroundColor: theme.evPalette.charcoal[100],
        padding: '13px',
        minWidth: '342px',
      },
    },
  },
  errorAlert: { backgroundColor: theme.evPalette.rose[100] },
  errorAlertIcon: { '&>svg': { color: theme.evPalette.rose[900] } },
  invalidEmail: {
    color: theme.evPalette.rose[900],
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '18px',
  },
  emailInputRoot: {
    height: 'unset',
    '&.error fieldset': { border: `1px solid ${theme.evPalette.rose[900]} !important` },
  },
  emailInputTag: {
    '&.error': { backgroundColor: theme.evPalette.rose[100] },
  },
}));

function EmailFilesWidget({
  reportId, selectedFiles, setShowEmailOptions, handleCancel,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [emailIDs, setEmailIDs] = useState([]);
  const [emailSent, setEmailSent] = useState('');

  const { reportDetails: reports, userId } = useSelector(
    ({ orderHistory: { reportDetails }, userData: { initParameters: { customerId } } }) => ({ reportDetails, userId: customerId }),
  );

  const { isReportMSO } = useMemo(
    () => reports?.find(({ reportId: id }) => reportId === id) || {},
    [reports, reportId],
  );

  const getFileDisplayName = (fileTypeName, fileFormatID) => `${fileTypeName} [${FILE_FORMAT_NAMES[fileFormatID]}]`;

  const handleEmailInputChange = (event, email) => {
    if (event?.type === 'change') setInputValue(email);
  };

  const handleEmailAdd = (list) => {
    setEmailIDs(list);
    setInputValue('');
  };

  const handleEmailDelete = (list) => setEmailIDs(list);

  const handleClear = (list) => {
    setEmailIDs(list);
    setInputValue('');
  };

  const hasInvalidEmailIDs = emailIDs.some((value) => !EMAIL_REGEX.test(value));

  const handleSendEmail = () => {
    emailReportFilesService({
      emailTo: emailIDs,
      reportFileIds: Object.keys(selectedFiles).map(Number),
      reportId,
      userId,
      isSitemap: Boolean(Number(isReportMSO)),
      fileFormat: '',
      isClaimSearch: false, // default value false as custom portals not in scope
      emailType: 27, // enum EmailType value
    }).then(({ ok }) => setEmailSent(ok ? SUCCESS : ERROR));
  };

  /**
   * Render the selected value.
   *
   * @param {T[]} value The `value` provided to the component.
   * @param {function} getTagProps A tag props getter.
   * @returns {ReactNode}
   */
  const renderTags = (values, getProps) => values.map((value, index) => {
    const { className, ...props } = getProps({ index });
    const invalid = !EMAIL_REGEX.test(value);

    return (
      <EVChip
        label={value}
        className={clsx(className, invalid && 'error')}
        deleteIcon={<EVClear className={clsx(classes.tagDeleteIcon, invalid && 'error')} />}
        {...props}
      />
    );
  });

  if (emailSent === SUCCESS) {
    return (
      <>
        <EVAlert
          severity={SUCCESS}
          classes={{ icon: classes.successAlertIcon, message: classes.alertMsg, root: classes.successAlert }}
          iconMapping={{ success: <EVCheckCircleOutlineIcon /> }}
        >
          {t('order_history__actions__send_email_success_alert')}
        </EVAlert>
        <EVGrid container direction="column" className={classes.successEmailList}>
          <EVTypography>{t('order_history__actions__send_email_success_text')}</EVTypography>
          {emailIDs.map((email) => <EVTypography key={email} className="listItem">{email}</EVTypography>)}
        </EVGrid>
      </>
    );
  }

  if (emailSent === ERROR) {
    return (
      <>
        <EVAlert severity={ERROR} classes={{ icon: classes.errorAlertIcon, message: classes.alertMsg, root: classes.errorAlert }}>
          {t('order_history__actions__send_email_failure_alert')}
        </EVAlert>
        <EVGrid container style={{ gap: '8px' }}>
          <EVButton color="primary" id="backBtn" variant="contained" onClick={() => setEmailSent('')}>{t('common_back')}</EVButton>
          <EVButton color="primary" id="cancelBtn" onClick={handleCancel}>{t('common_cancel')}</EVButton>
        </EVGrid>
      </>
    );
  }

  return (
    <>
      <EVTypography className="heading">{t('order_history__actions__email_files')}</EVTypography>
      <EVGrid container alignItems="center" className={classes.selectedFilesList}>
        {Object.values(selectedFiles).map(({ fileTypeName, fileFormatID }) => (
          <EVChip
            key={getFileDisplayName(fileTypeName, fileFormatID)}
            label={getFileDisplayName(fileTypeName, fileFormatID)}
            className={classes.selectedFileChip}
          />
        ))}
      </EVGrid>
      <EVTypography className={classes.enterEmailAddressText}>
        {t('order_history__actions__enter_email_address')}
      </EVTypography>
      <ChipInput
        id="emailIds-input"
        inputValue={inputValue}
        onInputChange={handleEmailInputChange}
        renderInput={(props) => <EVTextField variant="outlined" placeholder={t('order_history__actions__enter_email_address_placeholder')} {...props} />}
        onAddValue={handleEmailAdd}
        onDeleteValue={handleEmailDelete}
        onClear={handleClear}
        classes={{ inputRoot: clsx(classes.emailInputRoot, hasInvalidEmailIDs && 'error'), tag: classes.emailInputTag }}
        value={emailIDs}
        className={classes.emailInput}
        renderTags={renderTags}
      />
      {hasInvalidEmailIDs && (
      <EVFormHelperText className={classes.invalidEmail}>
        {t('order_history__actions__invalid_email_address')}
      </EVFormHelperText>
      )}
      <EVGrid container style={{ gap: '8px' }}>
        <EVButton
          variant="contained"
          color="primary"
          id={`sendEmail-${reportId}`}
          disabled={emailIDs.length === 0 || hasInvalidEmailIDs}
          className={classes.sendEmailBtn}
          onClick={handleSendEmail}
        >
          {t('common_send')}
        </EVButton>
        <EVButton color="primary" id={`cancelEmail-${reportId}`} onClick={() => setShowEmailOptions(false)}>{t('common_cancel')}</EVButton>
      </EVGrid>
    </>
  );
}

EmailFilesWidget.propTypes = {
  reportId: PropTypes.number.isRequired,
  selectedFiles: PropTypes.shape().isRequired,
  setShowEmailOptions: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
};

export default EmailFilesWidget;
